import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/tr';
import { KTSVG } from 'src/_metronic/helpers';

const API = `${process.env.REACT_APP_API_URL}`;

interface ReleaseNoteItem {
  id: number;
  title: string;
  content: string;
  permission_key: string | null;
  item_type: string;
  sort_order: number;
}

interface ReleaseNoteData {
  id: number;
  title: string;
  description: string;
  version: string | null;
  published_at: string;
  items: ReleaseNoteItem[];
}

const ITEM_TYPE_STYLES: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  feature: { label: 'Yeni Özellik', color: '#50CD89', bg: 'rgba(80,205,137,0.1)', icon: '✨' },
  improvement: { label: 'İyileştirme', color: '#009EF7', bg: 'rgba(0,158,247,0.1)', icon: '🔧' },
  bugfix: { label: 'Hata Düzeltme', color: '#F1416C', bg: 'rgba(241,65,108,0.1)', icon: '🐛' },
  info: { label: 'Bilgilendirme', color: '#FFA800', bg: 'rgba(255,168,0,0.1)', icon: 'ℹ️' },
};

export default function ReleaseNoteModal() {
  const [note, setNote] = useState<ReleaseNoteData | null>(null);
  const [visible, setVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/releasenote/unread`);
      if (res.data && res.data.id) {
        setNote(res.data);
        setVisible(true);
      }
    } catch {
      // sessizce devam et
    }
  }, []);

  useEffect(() => {
    // Sayfa yüklendikten 2 saniye sonra kontrol et
    const timer = setTimeout(() => {
      fetchUnread();
    }, 2000);
    return () => clearTimeout(timer);
  }, [fetchUnread]);

  const handleRead = async () => {
    if (!note) return;
    try {
      setActionLoading(true);
      await axios.post(`${API}/releasenote/mark-read/${note.id}`);
      setVisible(false);
      setNote(null);
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemindLater = async () => {
    if (!note) return;
    try {
      setActionLoading(true);
      await axios.post(`${API}/releasenote/mark-remind/${note.id}`);
      setVisible(false);
      setNote(null);
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  if (!visible || !note) return null;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="bg-white rounded-3 shadow-lg position-relative d-flex flex-column"
        style={{
          width: '95%',
          maxWidth: 700,
          maxHeight: '90vh',
          animation: 'fadeInUp 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div
          className="rounded-top d-flex align-items-center justify-content-between px-6 py-5"
          style={{
            background: 'linear-gradient(135deg, #3699FF 0%, #1B84FF 100%)',
            flexShrink: 0,
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: 44, height: 44, backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <span style={{ fontSize: '1.3rem' }}>🚀</span>
            </div>
            <div>
              <h4 className="text-white mb-0 fw-bolder">{note.title}</h4>
              <div className="d-flex align-items-center gap-2 mt-1">
                {note.version && (
                  <span className="badge fw-bold" style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: '0.75rem' }}>
                    {note.version}
                  </span>
                )}
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>{moment(note.published_at).format('DD MMMM YYYY')}</span>
              </div>
            </div>
          </div>
          <button
            className="btn btn-icon btn-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
            onClick={handleRemindLater}
            disabled={actionLoading}
            title="Daha sonra göster"
          >
            <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-3 svg-icon-white" />
          </button>
        </div>

        {/* Description */}
        {note.description && (
          <div className="px-6 pt-5 pb-2" style={{ flexShrink: 0 }}>
            <p className="text-gray-600 fs-7 mb-0">{note.description}</p>
          </div>
        )}

        {/* Items */}
        <div className="px-6 py-4 flex-grow-1" style={{ overflowY: 'auto' }}>
          {note.items.length === 0 ? (
            <div className="text-center text-muted py-8">İçerik bulunmuyor</div>
          ) : (
            <div className="d-flex flex-column gap-4">
              {note.items.map((item) => {
                const typeStyle = ITEM_TYPE_STYLES[item.item_type] || ITEM_TYPE_STYLES.feature;
                return (
                  <div
                    key={item.id}
                    className="rounded-2 p-4"
                    style={{
                      backgroundColor: typeStyle.bg,
                      borderLeft: `4px solid ${typeStyle.color}`,
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="badge px-2 py-1"
                        style={{
                          backgroundColor: typeStyle.color + '22',
                          color: typeStyle.color,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      >
                        {typeStyle.icon} {typeStyle.label}
                      </span>
                      <h6 className="fw-bolder text-gray-800 mb-0 fs-6">{item.title}</h6>
                    </div>
                    {item.content && <div className="text-gray-700 fs-7 release-note-content" dangerouslySetInnerHTML={{ __html: item.content }} style={{ lineHeight: 1.7 }} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 d-flex justify-content-between align-items-center border-top" style={{ flexShrink: 0 }}>
          <button className="btn btn-sm btn-light-warning fw-bold px-5" onClick={handleRemindLater} disabled={actionLoading}>
            {actionLoading ? <span className="spinner-border spinner-border-sm me-1" /> : null}⏰ Daha Sonra Hatırlat
          </button>
          <button className="btn btn-sm btn-primary fw-bold px-6" onClick={handleRead} disabled={actionLoading}>
            {actionLoading ? <span className="spinner-border spinner-border-sm me-1" /> : null}✓ Okudum, Anladım
          </button>
        </div>

        {/* Inline animation keyframes */}
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .release-note-content p { margin-bottom: 0.5rem; }
          .release-note-content ul, .release-note-content ol { padding-left: 1.2rem; margin-bottom: 0.5rem; }
          .release-note-content li { margin-bottom: 0.25rem; }
          .release-note-content h2, .release-note-content h3, .release-note-content h4 { margin-top: 0.5rem; margin-bottom: 0.3rem; font-weight: 700; }
          .release-note-content img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; }
          .release-note-content table { width: 100%; border-collapse: collapse; }
          .release-note-content td, .release-note-content th { border: 1px solid #e1e3ea; padding: 0.4rem 0.6rem; }
        `}</style>
      </div>
    </div>
  );
}
